import moment from 'moment';
import { CompositeModel } from './composite.model';
import { assignModelOptions } from '../../utilities/assign-model-options';
import { NotificationModel } from '../low-level/notification.model';
import { TimeModel } from '../primitive/time.model';
import { ODataObject } from '../../interfaces/odata-model.interface';
import { ConfigConstants } from '../../config/config-constants';
import { TextModel } from '../primitive/text.model';

export class ApprovedPCHDurationModel extends CompositeModel {
    public startDateTime: TimeModel;
    public endDateTime: TimeModel;
    public approver: TextModel;
    public approvedPCHDuration: PCHDateTimeRange;
    public noPCHAvailable = false;

    private static readonly CODE_GROUP = ConfigConstants.ApprovedPCHCodes.CODE_GROUP;
    private static readonly CODE_NAME = ConfigConstants.ApprovedPCHCodes.CODE_NAME;

    /**
     * Approved PCH model for updating PCH duration.
     *
     * @param notification current notification data
     */
    public constructor(private readonly notification: NotificationModel) {
        super('ApprovedPCHDurationModel');

        this.initializePCHDateTimes();

        this.approver = <TextModel>this.set(new TextModel('Approver'));
        this.approver = assignModelOptions(this.approver, { ...this.approver.options, required: true }, this);
    }

    private initializePCHDateTimes(): void {
        const entry = this.notification.codeSet.find(
            (code) =>
                code.codeGroup.value === ApprovedPCHDurationModel.CODE_GROUP &&
                code.codeName.value === ApprovedPCHDurationModel.CODE_NAME,
        );

        if (!entry) {
            this.noPCHAvailable = true;
            const now = moment();
            this.assignDateTime(now.clone(), now.clone());
            return;
        }

        const [startDateString, endDateString] = entry.codeDesc.value.split(';');
        const start = this.parseDateTime(startDateString);
        const end = this.parseDateTime(endDateString);

        this.assignDateTime(start, end);
        this.updateApprovedPCHDuration();
    }

    private parseDateTime(value: string): moment.Moment {
        const parsed = moment(value, ConfigConstants.formatPatternDateTimePCH);
        return parsed.isValid() ? parsed : moment();
    }

    private assignDateTime(start: moment.Moment, end: moment.Moment): void {
        this.startDateTime = <TimeModel>this.set(new TimeModel('StartDate', start));
        this.endDateTime = <TimeModel>this.set(new TimeModel('EndDate', end));
    }

    public setDateTimeOptions(): void {
        if (!this.startDateTime?.value) return;

        this.endDateTime = assignModelOptions(
            this.endDateTime,
            { ...this.endDateTime.options, minValue: this.startDateTime.value.clone() },
            this,
        );
    }

    public updateApprovedPCHDuration(): void {
        this.noPCHAvailable = false;

        this.approvedPCHDuration = {
            start: this.startDateTime.value.format(ConfigConstants.formatPatternDateTimeWithSeparator),
            end: this.endDateTime.value.format(ConfigConstants.formatPatternDateTimeWithSeparator),
        };
    }

    public getOutModel(): ODataApprovedPCHDurationOutModel {
        const notificationNo = this.notification.identifier.getQmnum();
        const taskNo = this.notification.identifier.getManum();

        return {
            MessageType: ConfigConstants.ODataPCHDurationTypeEnum.PCHW,
            Notification: notificationNo,
            Task: taskNo,
            FrontId: this.notification.identifier.getFrontId(),

            StartDate: this.startDateTime.value.format(ConfigConstants.formatSAPDate),
            StartTime: this.startDateTime.value.format(ConfigConstants.formatXSDTimeDuration),
            EndDate: this.endDateTime.value.format(ConfigConstants.formatSAPDate),
            EndTime: this.endDateTime.value.format(ConfigConstants.formatXSDTimeDuration),

            LongTextInfo: [
                {
                    Notification: notificationNo,
                    Task: taskNo,
                    Tdid: ConfigConstants.ODataPCHDurationTypeEnum.APPR,
                    Tdline: this.approver.value,
                },
            ],
        };
    }
}

export interface ODataApprovedPCHDurationOutModel extends ODataObject {
    MessageType: string;
    Notification: string;
    Task: string;
    FrontId: string;
    StartDate: string;
    StartTime: string;
    EndDate: string;
    EndTime: string;
    LongTextInfo: LongtextInfoEntry[];
}

export interface LongtextInfoEntry extends ODataObject {
    Notification: string;
    Task: string;
    Tdid: string;
    Tdline: string;
}

export interface ODataApprovedPCHDurationResponseModel extends ODataObject, ODataApprovedPCHDurationOutModel {
    __metadata: {
        id: string;
        uri: string;
        type: string;
    };
}

export type PCHDateTimeRange = {
    start: string;
    end: string;
};
